import { PlusIcon } from 'lucide-react';

import { cn, TableCell, TableHead } from '@crossnokaye/ux-common';

import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Stack } from '@/ux-common/components/stack/stack';

import { IconWithTooltip } from './icon-wrapper';

// Fixed width for the add stage column (enough to center a 16px icon with padding)
const ADD_STAGE_COLUMN_WIDTH = 'w-10 min-w-10';

interface AddStageColumnHeaderProps {
  /** Handler called when the plus icon is clicked */
  onClick: () => void;
}

/**
 * Header cell for the "Add Stage" column.
 * Displays a centered plus icon with "Add Stage" tooltip on hover.
 * Respects writeCommands permission.
 */
export const AddStageColumnHeader = ({ onClick }: AddStageColumnHeaderProps) => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  return (
    <TableHead
      className={cn(
        'p-0 border-b-1',
        ADD_STAGE_COLUMN_WIDTH,
        disabledByPermissions && 'opacity-50'
      )}
    >
      <div className="h-full flex items-center justify-center">
        <IconWithTooltip
          icon={PlusIcon}
          isDisabled={disabledByPermissions}
          delayDuration={0}
          onClick={() => {
            if (!disabledByPermissions) {
              onClick();
            }
          }}
          tooltipContent={
            <Stack className="stack-thin">
              <span className="font-bold">Add Stage</span>
              <span>{disabledByPermissions ? tooltipText : undefined}</span>
            </Stack>
          }
        />
      </div>
    </TableHead>
  );
};

interface AddStageColumnCellProps {
  /** Whether to render the cell (default: true) */
  show?: boolean;
}

/**
 * Empty cell for the "Add Stage" column in body rows.
 * Styled to match other empty cells in the sequencer table.
 * Use `show={false}` to conditionally hide the cell.
 */
export const AddStageColumnCell = ({ show = false }: AddStageColumnCellProps) => {
  if (!show) {
    return null;
  }

  return (
    <TableCell className={cn('p-0 h-[inherit] border-l-1 border-b-1', ADD_STAGE_COLUMN_WIDTH)} />
  );
};
